import {
    AfterViewInit,
    Component,
    Inject,
    InjectFlags,
    Injector,
    Input,
    OnDestroy,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {
    AbstractControl,
    ControlContainer,
    FormControl,
    FormGroup,
    FormGroupDirective,
    FormGroupName,
    ValidationErrors
} from '@angular/forms';
import { MatExpansionPanel } from '@angular/material/expansion';
import { KeyValuePair } from '@brokerportal/common/enums';
import { fromDictToKeyValuePairs } from '@brokerportal/common/utils';
import { FullStateNames, RegexPatterns } from 'app/constants';
import _ from 'lodash-es';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AddressSearchComponent } from './search/search.component';

export type AddressFormControlNames =
    | 'fullAddress'
    | 'unitNumber'
    | 'streetNumber'
    | 'street'
    | 'suburb'
    | 'postcode'
    | 'state';
export type AddressPlaceholderConfigs = { [key in AddressFormControlNames]?: string };
export type AddressValidationMessageConfigs = { [key in AddressFormControlNames]?: { [error: string]: string } };

const DefaultPlaceholderConfigs: AddressPlaceholderConfigs = {
    fullAddress: 'Enter an address to start searching',
    unitNumber: 'Unit number',
    streetNumber: 'Street number',
    street: 'Street name',
    suburb: 'Suburb',
    postcode: 'Postcode',
    state: 'State'
};

const DefaultValidationMessages: AddressValidationMessageConfigs = {
    fullAddress: {
        required: 'Please enter an address',
        selectedAddressRequired: 'Please select an address'
    },
    unitNumber: {
        required: 'Please enter a unit number'
    },
    streetNumber: {
        required: 'Please enter a street number'
    },
    street: {
        required: 'Please enter a street name'
    },
    suburb: {
        required: 'Please enter a suburb'
    },
    postcode: {
        required: 'Please enter a postcode'
    },
    state: {
        required: 'Please enter a state'
    }
};

@Component({
    selector: 'bp-address',
    templateUrl: './address.component.html',
    styleUrls: ['./address.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'bp-address'
    }
})
export class AddressComponent implements AfterViewInit, OnDestroy {
    @Input()
    get placeholderConfigs() {
        return this._placeholderConfigs;
    }
    set placeholderConfigs(val) {
        this._placeholderConfigs = _.merge({}, DefaultPlaceholderConfigs, val);
    }
    private _placeholderConfigs: AddressPlaceholderConfigs = DefaultPlaceholderConfigs;

    @Input()
    get errorMessageConfigs() {
        return this._errorMessageConfigs;
    }
    set errorMessageConfigs(val) {
        this._errorMessageConfigs = _.merge({}, DefaultValidationMessages, val);
    }
    private _errorMessageConfigs: AddressValidationMessageConfigs = DefaultValidationMessages;

    @Input() states: Array<KeyValuePair<string, string>> = fromDictToKeyValuePairs(FullStateNames);
    @Input() panelHeaderExpandedText = 'Enter the address details';
    @Input() panelHeaderCollapsedText = 'Or click here to enter the address manually';

    @ViewChild('innerFormGroup', { static: true }) private innerFormGroup: FormGroupDirective;
    @ViewChild('search') private searchComponent: AddressSearchComponent;
    @ViewChild('panel') public readonly inputFormSection: MatExpansionPanel;

    private _form: FormGroup;
    /**
     * Gets the form group associated with this component.
     */
    get form(): FormGroup {
        // This getter assumes formGroup input is always provided,
        // which should be the case if used as per parent template.
        // ngOnInit handles the case where it might not be.
        return this._form;
    }

    get value() {
        return this.form?.getRawValue();
    }

    private controlContainer: ControlContainer;
    readonly destroyed$ = new Subject<void>();

    constructor(@Inject(Injector) private readonly injector: Injector) {}

    ngOnInit() {
        // It can cause a circular dependency if injected in the constructor
        this.controlContainer = this.injector.get(ControlContainer, null, InjectFlags.Self);

        if (this.controlContainer) {
            switch (this.controlContainer.constructor) {
                case FormGroupDirective: {
                    this._form = (this.controlContainer as FormGroupDirective).form;
                    break;
                }
                case FormGroupName: {
                    const formGroupName = this.controlContainer as FormGroupName;
                    const rootFormGroup = formGroupName.formDirective as FormGroupDirective;
                    this._form = rootFormGroup.getFormGroup(formGroupName);
                    rootFormGroup.ngSubmit.pipe(takeUntil(this.destroyed$)).subscribe(event => {
                        this.innerFormGroup.onSubmit(event);
                    });
                    break;
                }
                default: {
                    break;
                }
            }
        } else {
            this._form = new FormGroup({
                fullAddress: new FormControl(null),
                unitNumber: new FormControl(null),
                streetNumber: new FormControl(null),
                street: new FormControl(null),
                suburb: new FormControl(null),
                postcode: new FormControl(null),
                state: new FormControl(null)
            });
        }
        if (this._form && !this._form.get('fullAddress').value) {
            const unitNumber = this._form.get('unitNumber')?.value ?? '';
            const streetNumber = this._form.get('streetNumber')?.value ?? '';
            let street = this._form.get('street')?.value ?? '';
            const suburb = this._form.get('suburb')?.value ?? '';
            const postCode = this._form.get('postcode')?.value ?? '';
            const state = this._form.get('state')?.value ?? '';

            street = street ? `${street},` : street;
            const parts: string[] = [unitNumber, streetNumber, street, suburb, state, postCode];
            const fullAddress = parts.reduce((acc, part) => acc + (part ? `${part} ` : ''), '').trim();
            if (fullAddress) {
                this._form.get('fullAddress').setValue(fullAddress);
            }
        }
    }

    ngAfterViewInit() {
        if (!this._form || !this.searchComponent) {
            return;
        }
        this.updateValidators();

        this.searchComponent.selectedValueChange.pipe(takeUntil(this.destroyed$)).subscribe(address => {
            if (!address) return;

            const formValue: any = {};

            let unitNumber = '';
            if (!address.streetNumber && address.lotNumber) {
                formValue.streetNumber = address.subdwelling;
            } else {
                formValue.streetNumber = address.streetNumber;
                unitNumber = address.subdwelling;
            }
            unitNumber += address.buildingName;
            formValue.unitNumber = unitNumber;
            formValue.street = address.street;
            formValue.suburb = address.locality;

            if (RegexPatterns.Postcode.test(address.postcode)) {
                formValue.postcode = address.postcode;
            }
            formValue.state = address.state;

            this.form.patchValue(formValue);
            this.form.markAllAsTouched();
            if (this.form.invalid) {
                this.inputFormSection?.open();
            }
        });
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    onPanelStateChange(isExpanded: boolean) {
        if (!this.form) return;

        const fullAddressCtrl = this.form.get('fullAddress');
        if (!fullAddressCtrl) {
            return;
        }

        if (isExpanded) {
            fullAddressCtrl.disable();
        } else {
            fullAddressCtrl.enable();
        }
    }

    _getPlaceholder(fieldName: string) {
        return this.placeholderConfigs[fieldName] || null;
    }

    _getFirstOrDefaultErrorMessage(fieldName: string) {
        if (!this.form) return 'Parent FormGroup not available';
        const ctrl = this.form.get(fieldName);
        if (!ctrl) return `'${fieldName}' not found.`;

        if (ctrl.errors) {
            const errorKeys = Object.keys(ctrl.errors);
            if (errorKeys.length) {
                return (
                    (this.errorMessageConfigs[fieldName] && this.errorMessageConfigs[fieldName][errorKeys[0]]) ||
                    `Please enter a valid '${fieldName}'value.`
                );
            }
        }
        return null;
    }

    private updateValidators() {
        if (!this.form) return;

        const fullAddressCtrl = this.form.get('fullAddress');
        if (!fullAddressCtrl) {
            console.warn("bp-address: 'fullAddress' control not found in the provided formGroup.");
            return;
        }

        const originalValidators = fullAddressCtrl.validator ? [fullAddressCtrl.validator] : [];

        const selectedAddressValidator = (ctrl: AbstractControl): ValidationErrors | null => {
            if (
                this.searchComponent &&
                !ctrl.pending &&
                ctrl.value &&
                this.searchComponent.results?.length > 0 &&
                !this.searchComponent.selectedValue
            ) {
                return { selectedAddressRequired: true };
            }
            return null;
        };

        fullAddressCtrl.valueChanges.pipe(takeUntil(this.destroyed$), debounceTime(50)).subscribe(() => {
            if (
                this.searchComponent &&
                this.searchComponent.results?.length === 0 &&
                !this.searchComponent.selectedValue
            ) {
                fullAddressCtrl.setValidators(originalValidators);
            } else {
                fullAddressCtrl.setValidators([...originalValidators, selectedAddressValidator]);
            }
            fullAddressCtrl.updateValueAndValidity({ emitEvent: false }); // Avoid infinite loop
        });

        if (this.searchComponent) {
            this.searchComponent.searchCompleted.pipe(takeUntil(this.destroyed$)).subscribe(() => {
                fullAddressCtrl.setValidators([...originalValidators, selectedAddressValidator]);
                fullAddressCtrl.updateValueAndValidity({ emitEvent: false });
            });
        }
    }
}
